// When the client ↔ rider booking chat is open — RIDER-039, RIDER-046, RIDER-047.
//
// `product-spec/04 Rider Operations/Rider Dispatch Lifecycle`: after Confirm
// Pick-Up, "enable Rider-Client Chat/Call in both directions"; after Confirm
// Drop-Off, "close and hide Rider-Client Chat/Call in both directions".
//
// `0135`'s `chat_channel_participant` enforces exactly that on booking_status —
// `en_route_to_client` is the only open state — and implements "close" as no
// access at all, READS INCLUDED. So a screen on either side of the conversation
// has to survive the thread returning zero rows mid-conversation with no error,
// which is indistinguishable from "nobody has said anything yet" unless it asks
// the booking status first. This is that question, shared so the client app and
// the partner app cannot answer it differently.
//
// ⚠ The SERVER is the authority. This explains the state on screen; it must
// never be used to decide access.

export type ClientRiderChatWindow =
  /** Before the delivery leg — the rider is still collecting the therapist. */
  | { state: "not_yet" }
  /** The delivery leg. Messages send and read. */
  | { state: "open" }
  /** Past Confirm Drop-Off, or never going to happen. The transcript is gone. */
  | { state: "closed" };

/**
 * The real `booking_status` labels past the window (0002, extended by 0054).
 * `cancelled` is closed rather than not_yet: it never reaches the window, and
 * saying chat "opens soon" would be a promise nothing can keep.
 */
const CLOSED_FROM = new Set(["arrived", "in_session", "completed", "cancelled"]);

export function clientRiderChatWindow(bookingStatus: string): ClientRiderChatWindow {
  if (bookingStatus === "en_route_to_client") return { state: "open" };
  if (CLOSED_FROM.has(bookingStatus)) return { state: "closed" };
  // Fail closed: an unrecognised status never presents a send box.
  return { state: "not_yet" };
}

// When the rider ↔ therapist booking chat is open — RIDER-045, RIDER-046.
//
// `product-spec/04 Rider Operations/Rider Dispatch Lifecycle`: while the rider
// is collecting the therapist "Rider and Therapist may Chat and Call"; after
// Confirm Pick-Up "hide the Therapist Chat/Call controls — it is replaced by
// Rider-Client Chat/Call". So `dispatched` is the only open state, and the
// moment the therapist confirms pick-up (`en_route_to_client`) the channel is
// already closed — there is no overlap between the two conversations.
//
// Same rules as the client ↔ rider window: `0135` makes the server the
// authority and implements close as no access at all, reads included. This
// explains the state on screen; it must never be used to decide access.

export type RiderTherapistChatWindow = ClientRiderChatWindow;

const RIDER_THERAPIST_CLOSED_FROM = new Set(["en_route_to_client", ...CLOSED_FROM]);

export function riderTherapistChatWindow(bookingStatus: string): RiderTherapistChatWindow {
  if (bookingStatus === "dispatched") return { state: "open" };
  if (RIDER_THERAPIST_CLOSED_FROM.has(bookingStatus)) return { state: "closed" };
  return { state: "not_yet" };
}
